/**
 * Microsoft Graph User Search Hook
 *
 * Provides typeahead search functionality for Entra ID users
 * using the Microsoft Graph API.
 */

import { InteractionRequiredAuthError, BrowserAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useState, useCallback } from 'react';

/**
 * Graph API user result
 */
export interface GraphUser {
  id: string;
  displayName: string;
  mail: string | null;
  userPrincipalName: string;
  jobTitle: string | null;
  department: string | null;
}

/**
 * Scopes required for user search
 * Note: User.Read.All is required to access jobTitle and department
 * User.ReadBasic.All only provides: displayName, givenName, surname, mail, id, photo, userPrincipalName
 */
const graphScopes = {
  scopes: ['User.Read.All'],
};

/**
 * Hook for searching users in Microsoft Graph
 */
export function useGraphUserSearch() {
  const { instance, accounts } = useMsal();
  const [users, setUsers] = useState<GraphUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Search for users by name or email
   */
  const searchUsers = useCallback(
    async (query: string): Promise<GraphUser[]> => {
      if (!query || query.length < 2) {
        setUsers([]);
        return [];
      }

      const account = accounts[0];
      if (!account) {
        setError('Not authenticated');
        return [];
      }

      setIsSearching(true);
      setError(null);

      try {
        // Acquire token for Microsoft Graph
        // First try silent, then fall back to popup if consent is needed
        let tokenResponse;
        try {
          tokenResponse = await instance.acquireTokenSilent({
            ...graphScopes,
            account,
          });
        } catch (silentError) {
          // If silent fails due to consent/interaction required, use popup
          if (silentError instanceof InteractionRequiredAuthError) {
            console.warn('Consent required, triggering interactive login...');
            try {
              tokenResponse = await instance.acquireTokenPopup({
                ...graphScopes,
                account,
              });
            } catch (popupError) {
              // If popup is blocked, fall back to redirect
              if (
                popupError instanceof BrowserAuthError &&
                (popupError.errorCode === 'popup_window_error' ||
                  popupError.errorCode === 'empty_window_error')
              ) {
                console.warn('Popup blocked, falling back to redirect...');
                // Store current location to return after redirect
                sessionStorage.setItem('graph_consent_redirect', window.location.pathname);
                await instance.acquireTokenRedirect({
                  ...graphScopes,
                  account,
                });
                // This line won't execute - page will redirect
                return [];
              }
              throw popupError;
            }
          } else {
            throw silentError;
          }
        }

        // Search users via Graph API
        // Using $filter to search by displayName or mail starting with the query
        const searchFilter = encodeURIComponent(
          `startswith(displayName,'${query}') or startswith(mail,'${query}') or startswith(userPrincipalName,'${query}')`
        );

        const response = await fetch(
          `https://graph.microsoft.com/v1.0/users?$filter=${searchFilter}&$top=10&$select=id,displayName,mail,userPrincipalName,jobTitle,department`,
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error?.message || `Graph API error: ${response.status}`);
        }

        const data = await response.json();
        const results: GraphUser[] = data.value || [];

        setUsers(results);
        return results;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to search users';
        console.error('Graph user search error:', err);
        setError(message);
        setUsers([]);
        return [];
      } finally {
        setIsSearching(false);
      }
    },
    [instance, accounts]
  );

  /**
   * Clear search results
   */
  const clearResults = useCallback(() => {
    setUsers([]);
    setError(null);
  }, []);

  return {
    users,
    isSearching,
    error,
    searchUsers,
    clearResults,
  };
}
